import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FiAlertTriangle, FiInfo, FiRefreshCw, FiX } from 'react-icons/fi';
import { BarChartHoverLabel } from '@/components/admin/BarChartHoverLabel';
import { track } from '@/lib/analytics';
import { apiJson } from '@/lib/api';
import type {
  AdminPerfEndpointRow,
  AdminPerfEndpointSeriesResponse,
  AdminPerfEndpointsResponse,
  AdminPerfOverviewPoint,
  AdminPerfOverviewResponse,
  AdminPerfRange,
  AdminPerfSort,
} from '@/lib/types';

const RANGE_OPTIONS: { range: AdminPerfRange; label: string; description: string }[] = [
  { range: '24h', label: '24h', description: 'Last 24 hours' },
  { range: '7d', label: '7d', description: 'Last 7 days' },
];

const ALLOWED_RANGES = new Set<string>(RANGE_OPTIONS.map((r) => r.range));

const SORT_OPTIONS: { sort: AdminPerfSort; label: string }[] = [
  { sort: 'count', label: 'Requests' },
  { sort: 'p95', label: 'p95 latency' },
  { sort: 'error_rate', label: 'Error rate' },
];

type ChartPoint = { label: string; sublabel: string; value: number };

type LatencySeriesPoint = {
  label: string;
  sublabel: string;
  avgMs: number;
  p95Ms: number;
  requestCount: number;
};

const AVG_LINE_COLOR = '#018A9D';
const P95_LINE_COLOR = '#6366f1';

type SelectedEndpoint = { method: string; template: string };

function endpointKey(row: SelectedEndpoint): string {
  return `${row.method} ${row.template}`;
}

function formatMs(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })}s`;
  return `${ms.toLocaleString(undefined, { maximumFractionDigits: ms < 10 ? 1 : 0 })}ms`;
}

function formatPercent(ratio: number): string {
  return `${(ratio * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

function formatInt(value: number): string {
  return value.toLocaleString();
}

function hourLabels(timestamp: string, range: AdminPerfRange): { label: string; sublabel: string } {
  const dt = new Date(timestamp);
  const valid = !Number.isNaN(dt.getTime());
  return {
    label: valid
      ? range === '24h'
        ? dt.toLocaleTimeString(undefined, { hour: 'numeric' })
        : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      : '',
    sublabel: valid
      ? dt.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
        })
      : timestamp,
  };
}

function buildPoints(
  points: AdminPerfOverviewResponse['points'],
  range: AdminPerfRange,
  pick: (p: AdminPerfOverviewResponse['points'][number]) => number,
): ChartPoint[] {
  return points.map((p) => {
    const { label, sublabel } = hourLabels(p.timestamp, range);
    return { label, sublabel, value: pick(p) };
  });
}

function buildLatencySeries(
  points: AdminPerfOverviewPoint[],
  range: AdminPerfRange,
): LatencySeriesPoint[] {
  return points.map((p) => {
    const { label, sublabel } = hourLabels(p.timestamp, range);
    return {
      label,
      sublabel,
      avgMs: p.avg_ms,
      p95Ms: p.p95_ms,
      requestCount: p.request_count,
    };
  });
}

function gappedLinePath(points: { x: number; y: number; hasData: boolean }[]): string {
  const parts: string[] = [];
  let drawing = false;
  for (const point of points) {
    if (!point.hasData) {
      drawing = false;
      continue;
    }
    parts.push(`${drawing ? 'L' : 'M'} ${point.x} ${point.y}`);
    drawing = true;
  }
  return parts.join(' ');
}

function niceAxisMax(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function xLabelIndices(count: number): Set<number> {
  if (count <= 1) return new Set([0]);
  if (count <= 12) return new Set(Array.from({ length: count }, (_, i) => i));
  const target = 8;
  const step = Math.max(1, Math.round((count - 1) / (target - 1)));
  const indices = new Set<number>([0]);
  for (let i = step; i < count - 1; i += step) indices.add(i);
  indices.add(count - 1);
  return indices;
}

function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className='rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm'>
      <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>{label}</p>
      <p className='mt-1 text-2xl font-semibold tabular-nums text-nova-foreground'>{value}</p>
      {hint ? <p className='mt-1 text-xs text-nova-muted-foreground'>{hint}</p> : null}
    </div>
  );
}

function PerfBarChart({
  title,
  subtitle,
  yAxisLabel,
  barColor,
  points,
  formatValue,
}: {
  title: string;
  subtitle: string;
  yAxisLabel: string;
  barColor: string;
  points: ChartPoint[];
  formatValue: (value: number) => string;
}) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const updateWidth = () => {
      const next = el.clientWidth;
      if (next > 0) setChartWidth(next);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const layout = useMemo(() => {
    if (!points.length || chartWidth <= 0) return null;
    const n = points.length;
    const barGap = n <= 24 ? 2 : 1;
    const padLeft = 56;
    const padRight = 12;
    const padTop = 16;
    const padBottom = 40;
    const width = chartWidth;
    const height = 260;
    const innerW = width - padLeft - padRight;
    const innerH = height - padTop - padBottom;
    const slotW = innerW / n;
    const barW = Math.max(2, slotW - barGap);
    const maxValue = Math.max(...points.map((p) => p.value), 0);
    const axisMax = niceAxisMax(maxValue);
    const labelIndices = xLabelIndices(n);
    return {
      n,
      barGap,
      barW,
      slotW,
      width,
      height,
      padLeft,
      padTop,
      padBottom,
      innerW,
      innerH,
      axisMax,
      labelIndices,
    };
  }, [points, chartWidth]);

  if (!points.length) {
    return <p className='text-sm text-nova-muted-foreground'>No data in this range.</p>;
  }

  const ticks = layout ? [0, 0.25, 0.5, 0.75, 1].map((f) => layout.axisMax * f) : [];
  const hoveredPoint = hoveredIndex !== null && layout ? points[hoveredIndex] : null;
  const hoveredBar =
    hoveredIndex !== null && layout
      ? (() => {
          const h =
            layout.axisMax > 0
              ? (points[hoveredIndex].value / layout.axisMax) * layout.innerH
              : 0;
          const x =
            layout.padLeft + hoveredIndex * layout.slotW + layout.barGap / 2;
          const y = layout.height - layout.padBottom - h;
          return { cx: x + layout.barW / 2, y };
        })()
      : null;
  const baselineY = layout ? layout.height - layout.padBottom : 0;

  return (
    <div className='min-w-0'>
      <div className='mb-3'>
        <p className='text-sm font-semibold text-nova-foreground'>{title}</p>
        <p className='text-xs text-nova-muted-foreground'>{subtitle}</p>
      </div>
      <div
        ref={chartRef}
        className='rounded-md border border-nova-border bg-nova-muted/50 pb-1'
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {layout ? (
          <svg
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            width='100%'
            height={layout.height}
            role='img'
            aria-label={`${title} bar chart`}
          >
            <text
              x={14}
              y={layout.padTop + layout.innerH / 2}
              transform={`rotate(-90 14 ${layout.padTop + layout.innerH / 2})`}
              textAnchor='middle'
              className='fill-gray-500 text-[11px] font-medium'
            >
              {yAxisLabel}
            </text>

            {ticks.map((tick) => {
              const y = baselineY - (tick / layout.axisMax) * layout.innerH;
              return (
                <g key={tick}>
                  <line
                    x1={layout.padLeft}
                    y1={y}
                    x2={layout.padLeft + layout.innerW}
                    y2={y}
                    stroke={tick === 0 ? '#d1d5db' : '#e5e7eb'}
                    strokeDasharray={tick === 0 ? undefined : '4 4'}
                  />
                  <text
                    x={layout.padLeft - 8}
                    y={y + 4}
                    textAnchor='end'
                    className='fill-gray-500 text-[10px] tabular-nums'
                  >
                    {formatValue(tick)}
                  </text>
                </g>
              );
            })}

            {points.map((p, i) => {
              const h = layout.axisMax > 0 ? (p.value / layout.axisMax) * layout.innerH : 0;
              const x = layout.padLeft + i * layout.slotW + layout.barGap / 2;
              const y = baselineY - h;
              const cx = x + layout.barW / 2;
              const isHovered = hoveredIndex === i;
              return (
                <g key={i}>
                  <rect
                    x={layout.padLeft + i * layout.slotW}
                    y={layout.padTop}
                    width={layout.slotW}
                    height={layout.innerH}
                    fill='transparent'
                    className='cursor-pointer'
                    onMouseEnter={() => setHoveredIndex(i)}
                  />
                  <rect
                    x={x}
                    y={y}
                    width={layout.barW}
                    height={Math.max(0, h)}
                    fill={barColor}
                    fillOpacity={isHovered ? 1 : hoveredIndex !== null ? 0.55 : 0.85}
                    rx={2}
                    className='pointer-events-none'
                  />
                  {layout.labelIndices.has(i) ? (
                    <text
                      x={cx}
                      y={baselineY + 14}
                      textAnchor='middle'
                      className='fill-gray-600 text-[10px] pointer-events-none'
                    >
                      {p.label}
                    </text>
                  ) : null}
                </g>
              );
            })}

            {hoveredPoint && hoveredBar ? (
              <BarChartHoverLabel
                x={hoveredBar.cx}
                barTopY={hoveredBar.y}
                minX={layout.padLeft + 30}
                maxX={layout.padLeft + layout.innerW - 30}
                minY={layout.padTop + 4}
                text={`${hoveredPoint.sublabel} · ${formatValue(hoveredPoint.value)}`}
              />
            ) : null}
          </svg>
        ) : (
          <div className='h-[260px]' />
        )}
      </div>
    </div>
  );
}

function EndpointLatencyLineChart({
  method,
  template,
  range,
  points,
  isLoading,
  isError,
  onClose,
}: {
  method: string;
  template: string;
  range: AdminPerfRange;
  points: LatencySeriesPoint[];
  isLoading: boolean;
  isError: boolean;
  onClose: () => void;
}) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const updateWidth = () => {
      const next = el.clientWidth;
      if (next > 0) setChartWidth(next);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const layout = useMemo(() => {
    if (!points.length || chartWidth <= 0) return null;
    const n = points.length;
    const padLeft = 56;
    const padRight = 12;
    const padTop = 16;
    const padBottom = 32;
    const width = chartWidth;
    const height = 180;
    const innerW = width - padLeft - padRight;
    const innerH = height - padTop - padBottom;
    const maxValue = Math.max(...points.map((p) => Math.max(p.avgMs, p.p95Ms)), 0);
    const axisMax = niceAxisMax(maxValue);
    const labelIndices = xLabelIndices(n);

    const mapped = points.map((point, i) => {
      const x = n <= 1 ? padLeft + innerW / 2 : padLeft + (i / (n - 1)) * innerW;
      const hasData = point.requestCount > 0;
      const avgY = padTop + innerH - (axisMax > 0 ? (point.avgMs / axisMax) * innerH : 0);
      const p95Y = padTop + innerH - (axisMax > 0 ? (point.p95Ms / axisMax) * innerH : 0);
      return { ...point, x, avgY, p95Y, hasData, index: i };
    });

    return {
      n,
      width,
      height,
      padLeft,
      padTop,
      padBottom,
      innerW,
      innerH,
      axisMax,
      labelIndices,
      mapped,
      avgPath: gappedLinePath(mapped.map((p) => ({ x: p.x, y: p.avgY, hasData: p.hasData }))),
      p95Path: gappedLinePath(mapped.map((p) => ({ x: p.x, y: p.p95Y, hasData: p.hasData }))),
    };
  }, [points, chartWidth]);

  const ticks = layout ? [0, 0.25, 0.5, 0.75, 1].map((f) => layout.axisMax * f) : [];
  const hovered = hoveredIndex !== null && layout ? layout.mapped[hoveredIndex] : null;
  const rangeLabel = range === '24h' ? 'last 24 hours' : 'last 7 days';

  return (
    <section className='rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm sm:p-4'>
      <div className='mb-3 flex flex-wrap items-start justify-between gap-2'>
        <div className='min-w-0'>
          <p className='text-sm font-semibold text-nova-foreground'>Endpoint latency</p>
          <p className='mt-0.5 flex flex-wrap items-center gap-2 text-xs text-nova-muted-foreground'>
            <span
              className={`inline-flex shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${methodBadgeClass(method)}`}
            >
              {method}
            </span>
            <span className='font-mono text-nova-foreground break-all'>{template}</span>
            <span>Avg and p95 over the {rangeLabel}</span>
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-3'>
          <div className='flex flex-wrap items-center gap-3 text-xs font-medium'>
            <span className='inline-flex items-center gap-1.5 text-teal-800'>
              <span
                className='inline-block h-2 w-2 rounded-full'
                style={{ backgroundColor: AVG_LINE_COLOR }}
              />
              Avg
            </span>
            <span className='inline-flex items-center gap-1.5 text-indigo-700'>
              <span
                className='inline-block h-2 w-2 rounded-full'
                style={{ backgroundColor: P95_LINE_COLOR }}
              />
              p95
            </span>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='inline-flex items-center gap-1 rounded-md border border-nova-input bg-nova-card px-2 py-1 text-xs font-semibold text-nova-muted-foreground hover:bg-nova-accent'
          >
            <FiX className='h-3.5 w-3.5' aria-hidden />
            Clear
          </button>
        </div>
      </div>

      <div
        ref={chartRef}
        className='rounded-md border border-nova-border bg-nova-muted/50 pb-1'
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {isError ? (
          <div className='flex h-[180px] items-center justify-center text-sm text-red-600'>
            Could not load endpoint latency.
          </div>
        ) : isLoading && !points.length ? (
          <div className='flex h-[180px] items-center justify-center'>
            <div
              className='h-6 w-6 animate-spin rounded-full border-2 border-nova-primary border-t-transparent'
              aria-label='Loading endpoint latency'
            />
          </div>
        ) : !points.length ? (
          <div className='flex h-[180px] items-center justify-center text-sm text-nova-muted-foreground'>
            No data in this range.
          </div>
        ) : layout ? (
          <svg
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            width='100%'
            height={layout.height}
            role='img'
            aria-label={`${method} ${template} avg and p95 latency line chart`}
          >
            <text
              x={14}
              y={layout.padTop + layout.innerH / 2}
              transform={`rotate(-90 14 ${layout.padTop + layout.innerH / 2})`}
              textAnchor='middle'
              className='fill-gray-500 text-[11px] font-medium'
            >
              Latency
            </text>

            {ticks.map((tick) => {
              const y =
                layout.padTop +
                layout.innerH -
                (layout.axisMax > 0 ? (tick / layout.axisMax) * layout.innerH : 0);
              return (
                <g key={tick}>
                  <line
                    x1={layout.padLeft}
                    y1={y}
                    x2={layout.padLeft + layout.innerW}
                    y2={y}
                    stroke={tick === 0 ? '#d1d5db' : '#e5e7eb'}
                    strokeDasharray={tick === 0 ? undefined : '4 4'}
                  />
                  <text
                    x={layout.padLeft - 8}
                    y={y + 4}
                    textAnchor='end'
                    className='fill-gray-500 text-[10px] tabular-nums'
                  >
                    {formatMs(tick)}
                  </text>
                </g>
              );
            })}

            {layout.p95Path ? (
              <path
                d={layout.p95Path}
                fill='none'
                stroke={P95_LINE_COLOR}
                strokeWidth={2}
                strokeLinejoin='round'
                strokeLinecap='round'
                className='pointer-events-none'
              />
            ) : null}
            {layout.avgPath ? (
              <path
                d={layout.avgPath}
                fill='none'
                stroke={AVG_LINE_COLOR}
                strokeWidth={2}
                strokeLinejoin='round'
                strokeLinecap='round'
                className='pointer-events-none'
              />
            ) : null}

            {layout.mapped.map((point) => {
              const isHovered = hoveredIndex === point.index;
              const slotLeft =
                layout.n <= 1
                  ? layout.padLeft
                  : layout.padLeft +
                    ((point.index - 0.5) / Math.max(layout.n - 1, 1)) * layout.innerW;
              const slotRight =
                layout.n <= 1
                  ? layout.padLeft + layout.innerW
                  : layout.padLeft +
                    ((point.index + 0.5) / Math.max(layout.n - 1, 1)) * layout.innerW;
              const slotX = Math.max(layout.padLeft, slotLeft);
              const slotW = Math.max(
                8,
                Math.min(layout.padLeft + layout.innerW, slotRight) - slotX,
              );

              return (
                <g key={point.index}>
                  <rect
                    x={slotX}
                    y={layout.padTop}
                    width={slotW}
                    height={layout.innerH}
                    fill='transparent'
                    className='cursor-pointer'
                    onMouseEnter={() => setHoveredIndex(point.index)}
                  />
                  {isHovered && point.hasData ? (
                    <>
                      <circle
                        cx={point.x}
                        cy={point.avgY}
                        r={4.5}
                        fill={AVG_LINE_COLOR}
                        className='pointer-events-none'
                      />
                      <circle
                        cx={point.x}
                        cy={point.p95Y}
                        r={4.5}
                        fill={P95_LINE_COLOR}
                        className='pointer-events-none'
                      />
                    </>
                  ) : null}
                  {layout.labelIndices.has(point.index) ? (
                    <text
                      x={point.x}
                      y={layout.height - 10}
                      textAnchor='middle'
                      className='fill-gray-600 text-[10px] pointer-events-none'
                    >
                      {point.label}
                    </text>
                  ) : null}
                </g>
              );
            })}

            {hovered ? (
              <g pointerEvents='none'>
                <line
                  x1={hovered.x}
                  y1={layout.padTop}
                  x2={hovered.x}
                  y2={layout.padTop + layout.innerH}
                  stroke='#9ca3af'
                  strokeOpacity={0.5}
                  strokeWidth={1}
                />
                {(() => {
                  const lines = hovered.hasData
                    ? [
                        hovered.sublabel,
                        `Avg ${formatMs(hovered.avgMs)}`,
                        `p95 ${formatMs(hovered.p95Ms)}`,
                        `${formatInt(hovered.requestCount)} req`,
                      ]
                    : [hovered.sublabel, 'No requests'];
                  const tooltipW = Math.max(132, ...lines.map((line) => line.length * 6.4 + 20));
                  const tooltipH = 16 + lines.length * 14;
                  const tooltipX = Math.min(
                    Math.max(hovered.x - tooltipW / 2, layout.padLeft),
                    layout.padLeft + layout.innerW - tooltipW,
                  );
                  const anchorY = hovered.hasData
                    ? Math.min(hovered.avgY, hovered.p95Y)
                    : layout.padTop + layout.innerH / 2;
                  const aboveY = anchorY - tooltipH - 8;
                  const tooltipY =
                    aboveY >= layout.padTop
                      ? aboveY
                      : Math.min(anchorY + 10, layout.padTop + layout.innerH - tooltipH);

                  return (
                    <>
                      <rect
                        x={tooltipX}
                        y={tooltipY}
                        width={tooltipW}
                        height={tooltipH}
                        rx={6}
                        fill='#111827'
                        opacity={0.92}
                      />
                      {lines.map((line, i) => (
                        <text
                          key={line}
                          x={tooltipX + 10}
                          y={tooltipY + 16 + i * 14}
                          className={`fill-white text-[11px] ${
                            i === 0 ? 'font-semibold' : 'tabular-nums'
                          }`}
                        >
                          {line}
                        </text>
                      ))}
                    </>
                  );
                })()}
              </g>
            ) : null}
          </svg>
        ) : (
          <div className='h-[180px]' />
        )}
      </div>
    </section>
  );
}

function methodBadgeClass(method: string): string {
  switch (method.toUpperCase()) {
    case 'GET':
      return 'bg-sky-100 text-sky-700';
    case 'POST':
      return 'bg-emerald-100 text-emerald-700';
    case 'PUT':
    case 'PATCH':
      return 'bg-amber-100 text-amber-700';
    case 'DELETE':
      return 'bg-rose-100 text-rose-700';
    default:
      return 'bg-nova-muted text-nova-foreground';
  }
}

function AdminPerformancePage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { range: rangeParam, method: methodParam, template: templateParam } = Route.useSearch();
  const range: AdminPerfRange = ALLOWED_RANGES.has(rangeParam ?? '')
    ? (rangeParam as AdminPerfRange)
    : '24h';
  const selected: SelectedEndpoint | null =
    methodParam && templateParam ? { method: methodParam, template: templateParam } : null;
  const [sort, setSort] = useState<AdminPerfSort>('count');

  const activeRangeOption =
    RANGE_OPTIONS.find((opt) => opt.range === range) ?? RANGE_OPTIONS[0];

  const overviewQuery = useQuery({
    queryKey: ['admin', 'performance', 'overview', range],
    queryFn: () =>
      apiJson<AdminPerfOverviewResponse>(
        getToken,
        `/v1/admin/performance/overview?range=${encodeURIComponent(range)}`,
      ),
  });

  const endpointsQuery = useQuery({
    queryKey: ['admin', 'performance', 'endpoints', range, sort],
    queryFn: () =>
      apiJson<AdminPerfEndpointsResponse>(
        getToken,
        `/v1/admin/performance/endpoints?range=${encodeURIComponent(range)}&sort=${encodeURIComponent(sort)}`,
      ),
  });

  const seriesQuery = useQuery({
    queryKey: ['admin', 'performance', 'endpoint', range, selected?.method, selected?.template],
    queryFn: () =>
      apiJson<AdminPerfEndpointSeriesResponse>(
        getToken,
        `/v1/admin/performance/endpoint?range=${encodeURIComponent(range)}&method=${encodeURIComponent(selected!.method)}&template=${encodeURIComponent(selected!.template)}`,
      ),
    enabled: selected !== null,
  });

  const overview = overviewQuery.data;
  const endpoints = endpointsQuery.data;
  const isLoading = overviewQuery.isLoading || endpointsQuery.isLoading;
  const isFetching =
    overviewQuery.isFetching || endpointsQuery.isFetching || seriesQuery.isFetching;

  const throughputPoints = useMemo(
    () => (overview ? buildPoints(overview.points, range, (p) => p.request_count) : []),
    [overview, range],
  );
  const avgLatencyPoints = useMemo(
    () => (overview ? buildPoints(overview.points, range, (p) => p.avg_ms) : []),
    [overview, range],
  );
  const latencyPoints = useMemo(
    () => (overview ? buildPoints(overview.points, range, (p) => p.p95_ms) : []),
    [overview, range],
  );
  const selectedSeriesPoints = useMemo(
    () => (seriesQuery.data ? buildLatencySeries(seriesQuery.data.points, range) : []),
    [seriesQuery.data, range],
  );

  const setSearch = (next: {
    range: AdminPerfRange;
    method?: string;
    template?: string;
  }) => {
    navigate({
      to: '/admin/performance',
      search: {
        range: next.range,
        method: next.method,
        template: next.template,
      },
    });
  };

  const setRange = (next: AdminPerfRange) => {
    setSearch({
      range: next,
      method: selected?.method,
      template: selected?.template,
    });
  };

  const selectEndpoint = (row: AdminPerfEndpointRow) => {
    const alreadySelected =
      selected !== null && endpointKey(selected) === endpointKey(row);
    if (alreadySelected) {
      setSearch({ range });
      return;
    }
    track('Performance Endpoint Clicked', {
      method: row.method,
      template: row.template,
      range,
    });
    setSearch({ range, method: row.method, template: row.template });
  };

  const clearSelectedEndpoint = () => {
    setSearch({ range });
  };

  const handleRefresh = () => {
    void overviewQuery.refetch();
    void endpointsQuery.refetch();
    if (selected) void seriesQuery.refetch();
  };

  const notConfigured = overview && !overview.configured;

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
        <div className='min-w-0'>
          <h1 className='text-xl font-semibold text-nova-foreground sm:text-2xl'>Performance</h1>
          <p className='mt-1 text-sm text-nova-muted-foreground'>
            Request throughput, latency, and error rates from the API timing middleware. Hourly
            buckets (UTC), backed by Redis.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <div className='inline-flex flex-col gap-1.5' role='group' aria-label='Time range'>
            <span className='text-[11px] font-semibold uppercase tracking-wide text-nova-muted-foreground px-0.5'>
              Time range
            </span>
            <div className='inline-flex rounded-lg border border-nova-input bg-nova-card p-1 shadow-sm'>
              {RANGE_OPTIONS.map((opt) => {
                const active = range === opt.range;
                return (
                  <button
                    key={opt.range}
                    type='button'
                    aria-pressed={active}
                    title={opt.description}
                    onClick={() => setRange(opt.range)}
                    className={`min-w-[3rem] rounded-md px-3 py-2 text-xs font-bold transition-all ${
                      active
                        ? 'bg-nova-primary text-white shadow-sm ring-2 ring-nova-ring/30'
                        : 'text-nova-muted-foreground hover:bg-nova-accent hover:text-nova-foreground'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            type='button'
            onClick={handleRefresh}
            disabled={isFetching}
            className='inline-flex items-center gap-1.5 self-end rounded-md border border-nova-input bg-nova-card px-3 py-2 text-xs font-semibold text-nova-foreground hover:bg-nova-accent disabled:opacity-60'
          >
            <FiRefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className='flex justify-center py-12'>
          <div className='h-10 w-10 animate-spin rounded-full border-2 border-nova-primary border-t-transparent' />
        </div>
      ) : (
        <>
          <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-nova-muted-foreground'>
            <span className='inline-flex items-center rounded-md bg-nova-primary/10 px-2.5 py-1 text-xs font-semibold text-nova-primary'>
              {activeRangeOption.label} · {activeRangeOption.description}
            </span>
            <span className='inline-flex items-center gap-1.5'>
              <FiInfo className='h-3.5 w-3.5 shrink-0' aria-hidden />
              Excludes static, health, and performance endpoints.
            </span>
          </div>

          {notConfigured ? (
            <div className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex gap-2'>
              <FiAlertTriangle className='mt-0.5 h-4 w-4 shrink-0' aria-hidden />
              <div className='min-w-0 space-y-1'>
                <p className='font-semibold'>Performance metrics unavailable</p>
                <p className='text-xs'>
                  <span className='font-mono'>REDIS_URL</span> is not configured on the API
                  deployment, so request metrics are not being recorded.
                </p>
              </div>
            </div>
          ) : null}

          {overviewQuery.isError || !overview ? (
            <p className='text-red-600'>Could not load performance overview.</p>
          ) : (
            <>
              <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6'>
                <StatTile label='Total requests' value={formatInt(overview.total_requests)} />
                <StatTile
                  label='Avg req/min'
                  value={overview.avg_requests_per_minute.toLocaleString(undefined, {
                    maximumFractionDigits: 1,
                  })}
                />
                <StatTile label='Avg latency' value={formatMs(overview.avg_ms)} />
                <StatTile label='p95 latency' value={formatMs(overview.p95_ms)} />
                <StatTile label='p99 latency' value={formatMs(overview.p99_ms)} />
                <StatTile
                  label='5xx error rate'
                  value={formatPercent(overview.error_rate)}
                  hint={`4xx: ${formatPercent(overview.client_error_rate)}`}
                />
              </div>

              <section className='rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm sm:p-4'>
                <PerfBarChart
                  title='Requests per hour'
                  subtitle='Total matched API requests per hour (UTC)'
                  yAxisLabel='Requests'
                  barColor='#018A9D'
                  points={throughputPoints}
                  formatValue={formatInt}
                />
              </section>

              <section className='rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm sm:p-4'>
                <PerfBarChart
                  title='Avg latency per hour'
                  subtitle='Mean request latency per hour (UTC)'
                  yAxisLabel='Latency'
                  barColor={AVG_LINE_COLOR}
                  points={avgLatencyPoints}
                  formatValue={formatMs}
                />
              </section>

              <section className='rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm sm:p-4'>
                <PerfBarChart
                  title='p95 latency per hour'
                  subtitle='95th percentile request latency per hour (UTC)'
                  yAxisLabel='Latency'
                  barColor='#6366f1'
                  points={latencyPoints}
                  formatValue={formatMs}
                />
              </section>
            </>
          )}

          {selected ? (
            <EndpointLatencyLineChart
              method={selected.method}
              template={selected.template}
              range={range}
              points={selectedSeriesPoints}
              isLoading={seriesQuery.isLoading || seriesQuery.isFetching}
              isError={seriesQuery.isError}
              onClose={clearSelectedEndpoint}
            />
          ) : null}

          <section className='rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm sm:p-4'>
            <div className='mb-3 flex flex-wrap items-center justify-between gap-2'>
              <div>
                <p className='text-sm font-semibold text-nova-foreground'>Endpoints</p>
                <p className='text-xs text-nova-muted-foreground'>
                  Aggregated over the selected range. Top 100 by the chosen sort. Click a
                  row to graph its latency.
                </p>
              </div>
              <div className='inline-flex rounded-lg border border-nova-input bg-nova-card p-1 shadow-sm'>
                {SORT_OPTIONS.map((opt) => {
                  const active = sort === opt.sort;
                  return (
                    <button
                      key={opt.sort}
                      type='button'
                      aria-pressed={active}
                      onClick={() => setSort(opt.sort)}
                      className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                        active
                          ? 'bg-nova-primary text-white shadow-sm'
                          : 'text-nova-muted-foreground hover:bg-nova-accent hover:text-nova-foreground'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {endpointsQuery.isError ? (
              <p className='text-red-600'>Could not load endpoint metrics.</p>
            ) : !endpoints || endpoints.endpoints.length === 0 ? (
              <p className='text-sm text-nova-muted-foreground'>No endpoint data in this range.</p>
            ) : (
              <div className='overflow-x-auto'>
                <table className='min-w-full text-sm'>
                  <thead>
                    <tr className='border-b border-nova-border text-left text-xs uppercase tracking-wide text-nova-muted-foreground'>
                      <th className='py-2 pr-3 font-semibold'>Endpoint</th>
                      <th className='py-2 px-3 text-right font-semibold'>Requests</th>
                      <th className='py-2 px-3 text-right font-semibold'>Avg</th>
                      <th className='py-2 px-3 text-right font-semibold'>p95</th>
                      <th className='py-2 pl-3 text-right font-semibold'>5xx rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpoints.endpoints.map((row) => {
                      const isSelected =
                        selected !== null && endpointKey(selected) === endpointKey(row);
                      return (
                        <tr
                          key={`${row.method} ${row.template}`}
                          className={`cursor-pointer border-b border-nova-border last:border-0 ${
                            isSelected ? 'bg-nova-primary/5' : 'hover:bg-nova-accent'
                          }`}
                          aria-selected={isSelected}
                          onClick={() => selectEndpoint(row)}
                        >
                        <td className='py-2 pr-3'>
                          <span className='flex items-center gap-2'>
                            <span
                              className={`inline-flex shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${methodBadgeClass(row.method)}`}
                            >
                              {row.method}
                            </span>
                            <span className='font-mono text-xs text-nova-foreground break-all'>
                              {row.template}
                            </span>
                          </span>
                        </td>
                        <td className='py-2 px-3 text-right tabular-nums text-nova-foreground'>
                          {formatInt(row.count)}
                        </td>
                        <td className='py-2 px-3 text-right tabular-nums text-nova-foreground'>
                          {formatMs(row.avg_ms)}
                        </td>
                        <td className='py-2 px-3 text-right tabular-nums text-nova-foreground'>
                          {formatMs(row.p95_ms)}
                        </td>
                        <td
                          className={`py-2 pl-3 text-right tabular-nums ${
                            row.error_rate > 0 ? 'font-semibold text-rose-600' : 'text-nova-foreground'
                          }`}
                        >
                          {formatPercent(row.error_rate)}
                        </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

type PerformanceSearch = {
  range: AdminPerfRange;
  method?: string;
  template?: string;
};

export const Route = createFileRoute('/admin/performance')({
  validateSearch: (search: Record<string, unknown>): PerformanceSearch => ({
    range:
      typeof search.range === 'string' && ALLOWED_RANGES.has(search.range)
        ? (search.range as AdminPerfRange)
        : ('24h' as AdminPerfRange),
    method:
      typeof search.method === 'string' && search.method.trim()
        ? search.method.trim()
        : undefined,
    template:
      typeof search.template === 'string' && search.template.trim()
        ? search.template.trim()
        : undefined,
  }),
  component: AdminPerformancePage,
});
